<template>
  <form
    novalidate
    class="rms-reservation-form"
    :class="templateClass"
    aria-labelledby="reservation-heading"
    @submit.prevent="onSubmit"
  >
    <p v-if="formError" class="rms-reservation-form__error" role="alert">{{ formError }}</p>
    <p v-if="formSuccess" class="rms-reservation-form__success" role="status">{{ formSuccess }}</p>

    <!-- Step 1: reservation details + send code -->
    <template v-if="step === 1">
      <div class="rms-reservation-form__field">
        <label for="reservation-pax" class="rms-reservation-form__label">{{ paxLabel || $t('reservation.pax') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          id="reservation-pax"
          v-model.number="pax"
          type="number"
          min="1"
          max="20"
          class="rms-reservation-form__input"
          :aria-invalid="!!fieldErrors.pax"
          :aria-describedby="fieldErrors.pax ? 'reservation-pax-error' : undefined"
        >
        <p id="reservation-pax-error" v-if="fieldErrors.pax" class="rms-reservation-form__field-error">{{ fieldErrors.pax }}</p>
      </div>

      <div class="rms-reservation-form__field">
        <label for="reservation-date" class="rms-reservation-form__label">{{ $t('reservation.date') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          id="reservation-date"
          v-model="date"
          type="date"
          class="rms-reservation-form__input"
          :min="minDate"
          :aria-invalid="!!fieldErrors.date"
          :aria-describedby="fieldErrors.date ? 'reservation-date-error' : undefined"
        >
        <p id="reservation-date-error" v-if="fieldErrors.date" class="rms-reservation-form__field-error">{{ fieldErrors.date }}</p>
      </div>

      <div class="rms-reservation-form__field">
        <label for="reservation-time" class="rms-reservation-form__label">{{ $t('reservation.time') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          id="reservation-time"
          v-model="time"
          type="time"
          class="rms-reservation-form__input"
          :aria-invalid="!!fieldErrors.time"
          :aria-describedby="fieldErrors.time ? 'reservation-time-error' : undefined"
        >
        <p id="reservation-time-error" v-if="fieldErrors.time" class="rms-reservation-form__field-error">{{ fieldErrors.time }}</p>
      </div>

      <div class="rms-reservation-form__field">
        <label for="reservation-name" class="rms-reservation-form__label">{{ $t('reservation.customerName') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          id="reservation-name"
          v-model="customerName"
          type="text"
          autocomplete="name"
          maxlength="150"
          class="rms-reservation-form__input"
          :aria-invalid="!!fieldErrors.customer_name"
          :aria-describedby="fieldErrors.customer_name ? 'reservation-name-error' : undefined"
        >
        <p id="reservation-name-error" v-if="fieldErrors.customer_name" class="rms-reservation-form__field-error">{{ fieldErrors.customer_name }}</p>
      </div>

      <div class="rms-reservation-form__field">
        <label for="reservation-mobile" class="rms-reservation-form__label">{{ $t('reservation.customerMobile') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          id="reservation-mobile"
          v-model="customerMobile"
          type="tel"
          autocomplete="tel"
          class="rms-reservation-form__input"
          :placeholder="$t('reservation.mobilePlaceholder')"
          :aria-invalid="!!fieldErrors.customer_mobile"
          :aria-describedby="fieldErrors.customer_mobile ? 'reservation-mobile-error' : undefined"
        >
        <p id="reservation-mobile-error" v-if="fieldErrors.customer_mobile" class="rms-reservation-form__field-error">{{ fieldErrors.customer_mobile }}</p>
      </div>

      <button
        type="submit"
        class="rms-reservation-form__submit"
        :disabled="submitting"
        :style="submitStyle"
      >
        {{ submitting ? $t('app.loading') : $t('reservation.sendVerificationCode') }}
      </button>
    </template>

    <!-- Step 2: enter code + confirm reservation -->
    <template v-else>
      <p id="reservation-code-desc" class="rms-reservation-form__step-intro">{{ $t('reservation.enterCodePrompt') }}</p>
      <div class="rms-reservation-form__field">
        <label for="reservation-code" class="rms-reservation-form__label">{{ $t('reservation.verificationCode') }} <span class="rms-reservation-form__required" aria-hidden="true">*</span></label>
        <input
          ref="codeInputRef"
          id="reservation-code"
          v-model.trim="verificationCode"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="6"
          class="rms-reservation-form__input rms-reservation-form__input--code"
          :placeholder="$t('reservation.codePlaceholder')"
          :aria-invalid="!!fieldErrors.verification_code"
          :aria-describedby="codeInputDescribedBy"
        >
        <p id="reservation-code-error" v-if="fieldErrors.verification_code" class="rms-reservation-form__field-error" role="alert">{{ fieldErrors.verification_code }}</p>
      </div>
      <div v-if="recaptchaEnabled && !recaptchaIsV3" class="rms-reservation-form__field">
        <div ref="recaptchaContainerRef" class="rms-reservation-form__recaptcha-widget" aria-label="reCAPTCHA" />
        <p v-if="fieldErrors.captcha_token" class="rms-reservation-form__field-error" role="alert">{{ fieldErrors.captcha_token }}</p>
      </div>
      <div class="rms-reservation-form__actions">
        <button
          type="submit"
          class="rms-reservation-form__submit"
          :disabled="submitting"
          :style="submitStyle"
        >
          {{ submitting ? $t('app.loading') : $t('reservation.confirmReservation') }}
        </button>
        <button
          type="button"
          class="rms-reservation-form__secondary"
          :disabled="requestingCode"
          @click="onRequestNewCode"
        >
          {{ requestingCode ? $t('app.loading') : $t('reservation.requestNewCode') }}
        </button>
      </div>
    </template>
  </form>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { reservationService, verificationService } from '@/services'
import { normalizeApiError } from '@/services'
import { useI18n } from 'vue-i18n'
import { scrollToFirstInvalidField } from '@/utils/form'
import { useRecaptcha } from '@/composables/useRecaptcha'

const props = defineProps({
  slug: { type: String, required: true },
  /** template-1 | template-2 | template-3 for styling */
  template: { type: String, default: 'template-1' },
  primaryColor: { type: String, default: null },
  /** Override for pax field label (e.g. "Party size" for services, "Number of guests" for restaurants). */
  paxLabel: { type: String, default: '' },
})

const { t } = useI18n()
const { recaptchaEnabled, recaptchaIsV3, loadScript, renderWidget, getToken: getRecaptchaToken, executeToken: executeRecaptchaToken, reset: resetRecaptcha } = useRecaptcha()
const recaptchaContainerRef = ref(null)

const codeInputRef = ref(null)
const step = ref(1)
const pax = ref(2)
const date = ref('')
const time = ref('')
const customerName = ref('')
const customerMobile = ref('')
const verificationToken = ref('')
const verificationCode = ref('')
const fieldErrors = ref({})
const formError = ref('')
const formSuccess = ref('')
const submitting = ref(false)
const requestingCode = ref(false)

const templateClass = computed(() => `rms-reservation-form--${props.template}`)
/** Submit button style: default visible background (--rms-accent or fallback); when primaryColor is set, use it and ensure contrasting text. */
function isLightColor(hex) {
  if (!hex || typeof hex !== 'string') return false
  const h = hex.replace(/^#/, '')
  if (h.length !== 6 && h.length !== 3) return false
  const r = h.length === 6 ? parseInt(h.slice(0, 2), 16) : parseInt(h[0] + h[0], 16)
  const g = h.length === 6 ? parseInt(h.slice(2, 4), 16) : parseInt(h[1] + h[1], 16)
  const b = h.length === 6 ? parseInt(h.slice(4, 6), 16) : parseInt(h[2] + h[2], 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.7
}
const submitStyle = computed(() => {
  const bg = props.primaryColor?.trim()
  if (!bg) return {} // use CSS default (var(--rms-accent))
  return {
    backgroundColor: bg,
    color: isLightColor(bg) ? '#0f172a' : '#fff',
  }
})
/** aria-describedby for code input: always include step intro, append error id when present */
const codeInputDescribedBy = computed(() =>
  fieldErrors.value.verification_code ? 'reservation-code-desc reservation-code-error' : 'reservation-code-desc'
)

/** YYYY-MM-DD for minimum booking date (2 days from today). */
const minDate = computed(() => {
  const d = new Date()
  d.setDate(d.getDate() + 2)
  return d.toISOString().slice(0, 10)
})

/** International mobile: optional +, then 10–15 digits. */
function isValidInternationalPhone(value) {
  if (typeof value !== 'string') return false
  const trimmed = value.trim().replace(/\s/g, '')
  return /^\+?[0-9]{10,15}$/.test(trimmed)
}

/** Exactly 6 digits. */
function isValidVerificationCode(value) {
  if (typeof value !== 'string') return false
  return /^[0-9]{6}$/.test(value.trim())
}

function validateStep1() {
  const err = {}
  const p = Number(pax.value)
  if (Number.isNaN(p) || p < 1 || p > 20) {
    err.pax = t('reservation.paxInvalid')
  }
  const d = (date.value ?? '').trim()
  if (!d) {
    err.date = t('reservation.dateRequired')
  } else if (d < minDate.value) {
    err.date = t('reservation.dateMinTwoDays')
  }
  const tm = (time.value ?? '').trim()
  if (!tm) err.time = t('reservation.timeRequired')
  const name = (customerName.value ?? '').trim()
  if (!name) err.customer_name = t('reservation.nameRequired')
  else if (name.length > 150) err.customer_name = t('reservation.nameMax')
  const mobile = (customerMobile.value ?? '').trim()
  if (!mobile) err.customer_mobile = t('reservation.mobileRequired')
  else if (!isValidInternationalPhone(mobile)) err.customer_mobile = t('reservation.mobileInvalidInternational')
  fieldErrors.value = err
  return Object.keys(err).length === 0
}

const RESERVATION_FIELD_ORDER = ['pax', 'date', 'time', 'customer_name', 'customer_mobile', 'verification_code', 'captcha_token']
const RESERVATION_FIELD_SELECTORS = {
  pax: '#reservation-pax',
  date: '#reservation-date',
  time: '#reservation-time',
  customer_name: '#reservation-name',
  customer_mobile: '#reservation-mobile',
  verification_code: '#reservation-code',
  captcha_token: '.rms-reservation-form__recaptcha-widget',
}

function validateStep2() {
  const err = {}
  const code = (verificationCode.value ?? '').trim()
  if (!code) {
    err.verification_code = t('reservation.codeRequired')
  } else if (!isValidVerificationCode(code)) {
    err.verification_code = t('reservation.codeInvalid')
  }
  if (recaptchaEnabled.value && !recaptchaIsV3.value && !getRecaptchaToken()) {
    err.captcha_token = t('public.captchaRequired')
  }
  fieldErrors.value = err
  return Object.keys(err).length === 0
}

/** Normalize to +country code form (add + if digits only). */
function normalizeMobile(value) {
  const s = (value ?? '').trim().replace(/\s/g, '')
  if (/^\+[0-9]{10,15}$/.test(s)) return s
  if (/^[0-9]{10,15}$/.test(s)) return `+${s}`
  return s
}

async function onSubmit() {
  formError.value = ''
  formSuccess.value = ''
  fieldErrors.value = {}

  if (step.value === 1) {
    if (!validateStep1()) {
      nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
      return
    }
    submitting.value = true
    try {
      const mobile = normalizeMobile(customerMobile.value)
      const res = await verificationService.requestCode({
        purpose: 'reservation',
        scope: props.slug,
        customer_mobile: mobile,
      })
      verificationToken.value = res?.verification_token ?? ''
      verificationCode.value = ''
      if (verificationToken.value) {
        step.value = 2
        nextTick(() => codeInputRef.value?.focus())
      } else {
        formError.value = t('reservation.errorGeneric')
      }
    } catch (e) {
      const norm = normalizeApiError(e)
      formError.value = norm.message ?? t('reservation.errorGeneric')
    } finally {
      submitting.value = false
    }
    return
  }

  // Step 2: confirm with code
  if (!validateStep2()) {
    nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
    return
  }
  let captchaToken = ''
  if (recaptchaEnabled.value) {
    captchaToken = recaptchaIsV3.value ? await executeRecaptchaToken('reservation') : getRecaptchaToken()
    if (!captchaToken) {
      fieldErrors.value = { captcha_token: t('public.captchaRequired') }
      nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
      return
    }
  }
  submitting.value = true
  try {
    const dateVal = (date.value ?? '').trim()
    const timeVal = (time.value ?? '').trim()
    const checkRes = await reservationService.check(props.slug, { date: dateVal, time: timeVal })
    if (checkRes && checkRes.reservations_open === false && checkRes.message) {
      fieldErrors.value = { date: checkRes.message }
      nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
      submitting.value = false
      return
    }
    const body = {
      pax: Number(pax.value),
      date: dateVal,
      time: timeVal,
      customer_name: (customerName.value ?? '').trim().slice(0, 150),
      customer_mobile: normalizeMobile(customerMobile.value),
      verification_token: verificationToken.value,
      verification_code: verificationCode.value.trim(),
      ...(captchaToken ? { captcha_token: captchaToken } : {}),
    }
    const res = await reservationService.submit(props.slug, body)
    formSuccess.value = res?.message ?? t('reservation.success')
    step.value = 1
    verificationToken.value = ''
    verificationCode.value = ''
    pax.value = 2
    date.value = ''
    time.value = ''
    customerName.value = ''
    customerMobile.value = ''
    if (recaptchaEnabled.value) resetRecaptcha()
  } catch (e) {
    const norm = normalizeApiError(e)
    const msg = typeof norm.message === 'string' ? norm.message : ''
    if (e?.response?.status === 422 && msg.includes('Invalid or expired')) {
      formError.value = t('reservation.codeInvalidOrExpired')
      verificationCode.value = ''
      fieldErrors.value = { verification_code: t('reservation.codeInvalidOrExpired') }
      nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
    } else if (e?.response?.status === 403) {
      formError.value = t('reservation.notAvailable')
    } else if (e?.response?.status === 422 && e?.response?.data?.errors) {
      const errors = e.response.data.errors
      const err = {}
      if (errors.verification_code?.[0]) err.verification_code = errors.verification_code[0]
      else if (errors.verification_token?.[0]) err.verification_code = errors.verification_token[0]
      if (errors.captcha_token?.[0]) err.captcha_token = errors.captcha_token[0]
      if (errors.pax?.[0]) err.pax = errors.pax[0]
      if (errors.date?.[0]) err.date = errors.date[0]
      if (errors.time?.[0]) err.time = errors.time[0]
      if (errors.customer_name?.[0]) err.customer_name = errors.customer_name[0]
      if (errors.customer_mobile?.[0]) err.customer_mobile = errors.customer_mobile[0]
      fieldErrors.value = err
      nextTick(() => scrollToFirstInvalidField(fieldErrors.value, RESERVATION_FIELD_ORDER, RESERVATION_FIELD_SELECTORS))
      const msg = (norm.message && String(norm.message).trim()) || ''
      formError.value = msg || t('reservation.errorGeneric')
      if (errors.captcha_token?.[0] && recaptchaEnabled.value) resetRecaptcha()
    } else {
      const msg = (norm.message && String(norm.message).trim()) || ''
      formError.value = msg || t('reservation.errorGeneric')
    }
  } finally {
    submitting.value = false
  }
}

async function onRequestNewCode() {
  formError.value = ''
  fieldErrors.value = {}
  const mobile = normalizeMobile(customerMobile.value)
  if (!mobile || !isValidInternationalPhone(mobile)) {
    formError.value = t('reservation.mobileInvalidInternational')
    return
  }
  requestingCode.value = true
  try {
    const res = await verificationService.requestCode({
      purpose: 'reservation',
      scope: props.slug,
      customer_mobile: mobile,
    })
    verificationToken.value = res?.verification_token ?? ''
    verificationCode.value = ''
  } catch (e) {
    const norm = normalizeApiError(e)
    const msg = (norm.message && String(norm.message).trim()) || ''
    formError.value = msg || t('reservation.errorGeneric')
  } finally {
    requestingCode.value = false
  }
}

watch([date, time, customerName, customerMobile, pax], () => {
  if (step.value !== 1) return
  if (formError.value || Object.keys(fieldErrors.value).length) {
    formError.value = ''
    fieldErrors.value = {}
  }
})
watch(verificationCode, () => {
  if (formError.value || fieldErrors.value.verification_code) {
    formError.value = ''
    fieldErrors.value = { ...fieldErrors.value, verification_code: undefined }
    if (Object.keys(fieldErrors.value).every((k) => !fieldErrors.value[k])) fieldErrors.value = {}
  }
})
// When step becomes 2 and reCAPTCHA is enabled: load script and render v2 widget if needed
watch(step, (val) => {
  if (val !== 2 || !recaptchaEnabled.value) return
  loadScript().then(() => {
    if (recaptchaIsV3.value) return
    nextTick(() => {
      const container = recaptchaContainerRef.value
      if (container) renderWidget(container).catch(() => {})
    })
  }).catch(() => {})
})
</script>

<style scoped>
.rms-reservation-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.rms-reservation-form__title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: inherit;
}
.rms-reservation-form__step-intro {
  font-size: 0.9375rem;
  margin: 0;
  color: inherit;
}
.rms-reservation-form__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: inherit;
}
.rms-reservation-form__required {
  color: #dc2626;
}
.rms-reservation-form__input {
  width: 100%;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  color: #0f172a;
  -webkit-appearance: none;
  appearance: none;
}
.rms-reservation-form__input--code {
  letter-spacing: 0.25em;
  text-align: center;
  max-width: 10rem;
}
.rms-reservation-form__input:focus {
  outline: 2px solid var(--rms-accent, #2563eb);
  outline-offset: 2px;
}
.rms-reservation-form__input[aria-invalid="true"] {
  border-color: #dc2626;
}
.rms-reservation-form__field-error {
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
  color: #dc2626;
}
.rms-reservation-form__error {
  font-size: 0.875rem;
  color: #dc2626;
  margin: 0;
}
.rms-reservation-form__success {
  font-size: 0.875rem;
  color: #059669;
  margin: 0;
}
.rms-reservation-form__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.25rem;
}
.rms-reservation-form__submit {
  min-height: 44px;
  padding: 0.625rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background-color: var(--rms-accent, #2563eb);
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: opacity 0.15s;
}
.rms-reservation-form__submit:hover:not(:disabled) {
  opacity: 0.9;
}
.rms-reservation-form__submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.rms-reservation-form__submit:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
.rms-reservation-form__secondary {
  min-height: 44px;
  padding: 0.625rem 1rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: inherit;
  background: transparent;
  border: 1px solid currentColor;
  border-radius: 0.5rem;
  cursor: pointer;
  opacity: 0.9;
}
.rms-reservation-form__secondary:hover:not(:disabled) {
  opacity: 1;
}
.rms-reservation-form__secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.rms-reservation-form__secondary:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
/* Template 2 overrides */
.rms-reservation-form--template-2 .rms-reservation-form__input {
  border-color: #1e293b;
  background: #f5f5f0;
  color: #1e293b;
}
.rms-reservation-form--template-3 .rms-reservation-form__input {
  border-color: rgba(255,255,255,0.2);
  background: rgba(15,23,42,0.5);
  color: #f1f5f9;
}
.rms-reservation-form--template-3 .rms-reservation-form__success {
  color: #34d399;
}
</style>
